import { useEffect, useMemo, useRef, useState } from "react";
import { Paper } from "@mui/material";
import ReactSpeedometer from "react-d3-speedometer";
import api from "../../utils/api";
import { formatNumber } from "../../utils/formatNumber";
import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";
import {
  useLiveRanking,
  RANKING_POLL_MS,
  hhmm,
  candidateChannelIds,
  selectedChannelIds,
  writeChannelSelection,
} from "../../hooks/useLiveRanking";
import "./ChannelLiveRanking.css";

// Live leaderboard. The ranking itself lives in useLiveRanking, shared with the
// Overview card; what is added here is the schedule and the channel picker.

const ROW_HEIGHT = 52;
const EPG_CACHE_PREFIX = "lr_epg_";
const EPG_CACHE_MS = 60 * 60 * 1000;

const HIDE_OWN_PREFIX = "adm_lr_hideown_";

// The dial is kept but not drawn. Flip to bring it back; everything it needs —
// the board total, the own value, the share — is still computed below.
const SHOW_GAUGE = false;

function readHideOwn(channelId) {
  try {
    return localStorage.getItem(HIDE_OWN_PREFIX + channelId) === "1";
  } catch {
    return false;
  }
}

function writeHideOwn(channelId, hidden) {
  try {
    if (hidden) localStorage.setItem(HIDE_OWN_PREFIX + channelId, "1");
    else localStorage.removeItem(HIDE_OWN_PREFIX + channelId);
  } catch {
    /* private mode — the toggle still works for this visit */
  }
}

function todayISO() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate()
  ).padStart(2, "0")}`;
}

function minuteOfDay(value) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  return d.getUTCHours() * 60 + d.getUTCMinutes();
}

function readEpgCache(id, day) {
  try {
    const raw = sessionStorage.getItem(`${EPG_CACHE_PREFIX}${id}_${day}`);
    if (!raw) return null;
    const { blocks, at } = JSON.parse(raw);
    return Date.now() - at < EPG_CACHE_MS ? blocks : null;
  } catch {
    return null;
  }
}

function writeEpgCache(id, day, blocks) {
  try {
    sessionStorage.setItem(
      `${EPG_CACHE_PREFIX}${id}_${day}`,
      JSON.stringify({ blocks, at: Date.now() })
    );
  } catch {
    /* private mode, or the quota is full — the board just refetches */
  }
}

export default function ChannelLiveRanking({ channelId, refreshKey }) {
  const pool = useMemo(() => candidateChannelIds(channelId), [channelId]);
  const [selected, setSelected] = useState(() => selectedChannelIds(channelId));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [hideOwn, setHideOwn] = useState(() => readHideOwn(channelId));

  useEffect(() => {
    setSelected(selectedChannelIds(channelId));
    setHideOwn(readHideOwn(channelId));
    setPickerOpen(false);
  }, [channelId]);

  const { rows, ids, nowMinute, loading, lastUpdated } = useLiveRanking(
    channelId,
    refreshKey,
    selected
  );
  const [epgById, setEpgById] = useState({});

  const toggleChannel = (id) => {
    if (id === channelId) return;
    setSelected((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      const ordered = pool.filter((x) => next.includes(x));
      writeChannelSelection(channelId, ordered);
      return ordered;
    });
  };

  const toggleHideOwn = () => {
    setHideOwn((prev) => {
      writeHideOwn(channelId, !prev);
      return !prev;
    });
  };

  const isDark = useMemo(() => {
    try {
      return !!JSON.parse(localStorage.getItem("adm_panel_user") || "{}").darkmode;
    } catch {
      return false;
    }
  }, []);

  const { logPageChange } = useUserJourneyLogger({ channelId });
  useEffect(() => {
    logPageChange({ chartType: "liveranking" });
  }, [logPageChange]);

  /* ---------- what is on air ---------- */

  useEffect(() => {
    if (!ids.length) return;
    const ac = new AbortController();
    let alive = true;
    const day = todayISO();

    Promise.all(
      ids.map(async (id) => {
        const cached = readEpgCache(id, day);
        if (cached) return [id, cached];

        try {
          const res = await api.get(
            `/api/EpgLogs?channel_id=${encodeURIComponent(id)}&key_date=${day}`,
            { signal: ac.signal }
          );
          const blocks = (Array.isArray(res.data) ? res.data : [])
            .map((x) => ({
              name: x.program_name || x.ProgramName || "",
              start: minuteOfDay(x.start_date || x.StartDate),
              end: minuteOfDay(x.end_date || x.EndDate),
            }))
            .filter((b) => b.name && b.start !== null && b.end !== null);

          writeEpgCache(id, day, blocks);
          return [id, blocks];
        } catch {
          // A channel with no schedule loaded is normal, not an error.
          return [id, []];
        }
      })
    ).then((pairs) => {
      if (!alive) return;
      setEpgById((prev) => ({ ...prev, ...Object.fromEntries(pairs) }));
    });

    return () => {
      alive = false;
      ac.abort();
    };
  }, [ids]);

  const programById = useMemo(() => {
    const out = {};
    for (const id of ids) {
      // A programme past midnight ends on a lower minute than it starts.
      const onAir = (epgById[id] || []).find((b) =>
        b.end >= b.start
          ? nowMinute >= b.start && nowMinute < b.end
          : nowMinute >= b.start || nowMinute < b.end
      );
      out[id] = onAir
        ? { name: onAir.name, time: `${hhmm(onAir.start)}–${hhmm(onAir.end)}` }
        : null;
    }
    return out;
  }, [ids, epgById, nowMinute]);

  /* ---------- the picker's names ---------- */

  // A switched-off channel is not in the board's own name map.
  const [poolNames, setPoolNames] = useState({});

  // Tracked in a ref, not by looking for missing names: /ci returns no row for
  // an id it does not know, so the effect would refetch on its own results.
  const namesAsked = useRef(false);
  useEffect(() => {
    namesAsked.current = false;
    setPoolNames({});
  }, [channelId]);

  useEffect(() => {
    if (!pickerOpen || !pool.length || namesAsked.current) return;
    namesAsked.current = true;

    const ac = new AbortController();
    api
      .get(`/ci?i=${pool.map(encodeURIComponent).join("-")}`, { signal: ac.signal })
      .then((res) => {
        if (!Array.isArray(res.data)) return;
        const map = {};
        for (const info of res.data) {
          const id = String(info?.i || "");
          if (id) map[id] = (info.n || "").trim() || id;
        }
        setPoolNames(map);
      })
      .catch(() => {
        namesAsked.current = false;
      });

    return () => ac.abort();
  }, [pickerOpen, pool]);

  /* ---------- the board ---------- */

  // Rank and place are worked out over what is shown, so hiding a channel
  // leaves no gap. rows is already in ranking order.
  const board = useMemo(() => {
    const shown = hideOwn ? rows.filter((r) => !r.isOwn) : rows;
    let rank = 0;
    return shown.map((row, i) => ({
      ...row,
      rank: row.offAir ? null : ++rank,
      position: i + 1,
    }));
  }, [rows, hideOwn]);

  // Live channels only: an off-air leftover would flatten every real bar.
  const max = Math.max(...board.filter((r) => !r.offAir).map((r) => r.value), 1);
  const liveCount = board.filter((r) => !r.offAir).length;

  // Stable DOM order, moved into place, so overtaking slides rather than jumps.
  const byId = useMemo(() => [...board].sort((a, b) => a.id.localeCompare(b.id)), [board]);

  /* ---------- the dial ---------- */

  // Own figure against the board's total. The full ranking is used, not the
  // visible board: hiding a row must not change the arithmetic.
  const boardTotal = rows.reduce((sum, r) => sum + (r.offAir ? 0 : r.value), 0);
  const ownRow = rows.find((r) => r.isOwn);
  const ownValue = ownRow && !ownRow.offAir ? ownRow.value : 0;
  const gaugeReady = boardTotal > 0;
  const ownShare = gaugeReady ? Math.round((ownValue / boardTotal) * 100) : 0;

  return (
    <Paper className="lr-root" elevation={0}>
      <div className="lr-header">
        <div className="lr-header-main">
          <h2 className="lr-title">IntelTube live ranking</h2>
          <span className="lr-subtitle">
            {liveCount} of {board.length} on air · updates every {RANKING_POLL_MS / 1000}s
            {lastUpdated && ` · last ${lastUpdated.toLocaleTimeString()}`}
            {loading && " · refreshing"}
          </span>

          <div className="lr-controls">
            <button
              type="button"
              className={`lr-ctl ${pickerOpen ? "is-on" : ""}`}
              onClick={() => setPickerOpen((open) => !open)}
              aria-expanded={pickerOpen}
            >
              <i className="fa-solid fa-list-check" />
              Channels ({selected.length}/{pool.length})
            </button>

            <button
              type="button"
              className={`lr-ctl ${hideOwn ? "is-on" : ""}`}
              onClick={toggleHideOwn}
            >
              <i className={`fa-solid ${hideOwn ? "fa-eye-slash" : "fa-eye"}`} />
              {hideOwn ? "Own channel hidden" : "Hide own channel"}
            </button>
          </div>

          {pickerOpen && (
            <div className="lr-picker">
              {pool.map((id) => {
                const isOwn = id === channelId;
                return (
                  <label key={id} className={`lr-picker-row ${isOwn ? "is-own" : ""}`}>
                    <input
                      type="checkbox"
                      checked={selected.includes(id)}
                      disabled={isOwn}
                      onChange={() => toggleChannel(id)}
                    />
                    <span className="lr-picker-name">{poolNames[id] || id}</span>
                    {isOwn && <span className="lr-picker-tag">this channel</span>}
                  </label>
                );
              })}
              {pool.length === 0 && <span className="lr-picker-empty">No channels available.</span>}
            </div>
          )}
        </div>

        {SHOW_GAUGE && (
        <div className="lr-gauge">
          <div className="lr-gauge-title">Share of the board</div>
          {gaugeReady ? (
            <ReactSpeedometer
              // The library animates the needle but never redraws the dial on
              // a prop change, so a moving ceiling needs a remount.
              key={boardTotal}
              value={Math.min(ownValue, boardTotal)}
              minValue={0}
              maxValue={boardTotal}
              segments={4}
              // start/end interpolation runs the ring through magenta.
              segmentColors={["#bfdbfe", "#7dabff", "#3b82f6", "#1d4ed8"]}
              // 1 marks only the two ends; more gives six-figure decimals at
              // uneven fractions of the maximum.
              maxSegmentLabels={1}
              labelFontSize="11px"
              needleColor={isDark ? "#e2e8f0" : "#374151"}
              textColor={isDark ? "#cbd5e1" : "#6b7280"}
              ringWidth={20}
              width={240}
              // Not a free choice. The library puts the dial's centre at
              // (width + 4·paddingVertical) / 2 — derived from the width, not
              // the height — and prints the reading 23px below that. At this
              // width the reading's baseline lands near 151px, so anything
              // under ~165px cuts the number in half. Widen the dial and this
              // has to grow with it.
              height={175}
              valueTextFontSize="20px"
              paddingVertical={4}
              // Room for the end labels, which otherwise get cut off.
              paddingHorizontal={14}
              needleTransitionDuration={800}
              // Thousands separators on the needle reading.
              valueFormat=","
            />
          ) : (
            <div className="lr-gauge-empty">
              {loading ? "…" : "Nothing on air to measure against"}
            </div>
          )}
          {gaugeReady && (
            <div className="lr-gauge-foot">
              {formatNumber(ownValue)} of {formatNumber(boardTotal)} · {ownShare}%
            </div>
          )}
        </div>
        )}
      </div>

      <div className="lr-board" style={{ height: board.length * ROW_HEIGHT }}>
        {byId.map((row) => {
          const program = programById[row.id];
          return (
            <div
              key={row.id}
              // TV blue, YouTube red, own channel amber wherever it sits.
              className={`lr-row ${
                row.source ? `lr-row--${row.source.toLowerCase()}` : ""
              } ${row.isOwn ? "is-own" : ""} ${row.offAir ? "is-offair" : ""}`}
              style={{ transform: `translateY(${(row.position - 1) * ROW_HEIGHT}px)` }}
            >
              <span className="lr-rank">{row.rank ?? "–"}</span>
              {row.logo ? (
                <img className="lr-logo" src={row.logo} alt="" />
              ) : (
                <span className="lr-logo lr-logo--blank" />
              )}
              <span className="lr-names">
                <span className="lr-name" title={row.name}>
                  {row.name}
                </span>
                <span className="lr-program" title={program?.name || ""}>
                  {program?.time && <span className="lr-program-time">{program.time}</span>}
                  {program?.name || "—"}
                </span>
              </span>
              {/* Empty when nothing is reported, but the column is kept. */}
              <span
                className={`lr-source ${
                  row.source ? `lr-source--${row.source.toLowerCase()}` : ""
                }`}
              >
                {row.source}
              </span>
              <span className="lr-track">
                <span
                  className="lr-bar"
                  style={{ width: `${row.offAir ? 0 : (row.value / max) * 100}%` }}
                />
              </span>
              <span className="lr-value">
                {row.offAir ? (
                  <span
                    className="lr-offair"
                    title={row.lastSeen && `last seen ${row.lastSeen}`}
                  >
                    off air{row.lastSeen ? ` · ${row.lastSeen}` : ""}
                  </span>
                ) : (
                  formatNumber(row.value)
                )}
              </span>
            </div>
          );
        })}

        {rows.length === 0 && <div className="lr-empty">No channels to rank yet.</div>}
      </div>
    </Paper>
  );
}
